
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/auth"; // Updated import
import { Plan, UserOrg } from "@/types/subscription";
import { 
  fetchSubscriptionPlans, 
  fetchUserOrganizations,
  updateOrganizationPlan
} from "@/services/subscription"; // Updated import path

export const usePricingPlans = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isYearly, setIsYearly] = useState<boolean>(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [userOrgs, setUserOrgs] = useState<UserOrg[]>([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch plans and user organizations
  useEffect(() => {
    const loadInitialData = async () => {
      if (!user) {
        navigate("/login");
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Charger les plans d'abonnement
        const plansData = await fetchSubscriptionPlans();
        setPlans(plansData);
        
        // Charger les organisations de l'utilisateur
        const userOrganizations = await fetchUserOrganizations(user.id);
        
        if (userOrganizations.length > 0) {
          setUserOrgs(userOrganizations);
          setSelectedOrgId(userOrganizations[0].id);
        } else {
          handleNoOrganizations();
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadInitialData();
  }, [user, navigate, toast]);

  /**
   * Gère le cas où l'utilisateur n'a pas d'organisation
   */
  const handleNoOrganizations = () => {
    console.log("Aucune organisation trouvée pour l'utilisateur");
    toast({
      title: "Aucune organisation",
      description: "Vous devez créer une organisation avant de choisir un plan.",
      variant: "destructive",
    });
    setTimeout(() => navigate("/organization/setup"), 2000);
  };

  /**
   * Gère les erreurs qui peuvent survenir lors du chargement des données
   */
  const handleError = (error: any) => {
    console.error("Erreur lors du chargement des données:", error);
    toast({
      title: "Erreur",
      description: error.message || "Impossible de charger les informations nécessaires.",
      variant: "destructive",
    });
  };

  /**
   * Gère la sélection d'un plan par l'utilisateur
   */
  const handleSelectPlan = async (planId: string) => {
    if (!user) {
      toast({
        title: "Erreur",
        description: "Vous devez être connecté pour souscrire à un plan.",
        variant: "destructive",
      });
      navigate("/login");
      return;
    }

    if (!selectedOrgId) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner une organisation.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsLoading(true);
      
      await updateOrganizationPlan(selectedOrgId, user.id, planId, isYearly);
      
      // Trouver le plan sélectionné pour afficher son nom
      const selectedPlan = plans.find(p => p.id === planId);
      
      // Notifier l'utilisateur du succès et rediriger
      toast({
        title: "Plan mis à jour",
        description: `Votre organisation est maintenant sur le plan ${selectedPlan?.displayName || "sélectionné"}.`,
        variant: "default",
      });
      
      // Redirection vers le dashboard après 2 secondes
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (error: any) {
      console.error("Erreur lors de la mise à jour du plan:", error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de mettre à jour le plan d'abonnement.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    plans,
    isLoading,
    isYearly,
    setIsYearly,
    selectedOrgId,
    setSelectedOrgId,
    userOrgs,
    handleSelectPlan
  };
};
